import { useState, useEffect, useCallback } from 'react'
import { biApi } from '../../api/bi'
import { colors, font, sx } from '../../design/tokens'
import { fmtAbrev, fmtNum, PALETA_BI, presetRangoBi, PRESETS_BI } from '../../utils/bi'

const mono = sx.mono

// ─── Variación ───────────────────────────────────────────────────────
function Delta({ pct }) {
  if (pct === null || pct === undefined) return null
  const up = pct >= 0
  return <span style={{ fontSize: 11, fontWeight: 700, color: up ? colors.ok : colors.err, ...mono }}>{up ? '▲' : '▼'}{Math.abs(pct).toFixed(1)}%</span>
}

function Kpi({ label, valor, delta }) {
  return (
    <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: colors.text, ...mono, margin: '3px 0 2px' }}>{valor}</div>
      <Delta pct={delta} />
    </div>
  )
}

// ─── Línea de tendencia (responsive) ─────────────────────────────────
function MiniLinea({ datos }) {
  const W = 340, H = 150, P = { t: 12, r: 8, b: 20, l: 40 }
  if (!datos.length) return <Vacio />
  const max = Math.max(...datos.map((d) => d.facturacion), 1)
  const iw = W - P.l - P.r, ih = H - P.t - P.b
  const x = (i) => P.l + (datos.length === 1 ? iw / 2 : (i / (datos.length - 1)) * iw)
  const y = (v) => P.t + ih - (v / max) * ih
  const linea = datos.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(d.facturacion).toFixed(1)}`).join(' ')
  const area = `${linea} L ${x(datos.length - 1).toFixed(1)} ${(P.t + ih).toFixed(1)} L ${x(0).toFixed(1)} ${(P.t + ih).toFixed(1)} Z`
  const cadaN = Math.ceil(datos.length / 5)
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      {[0, 0.5, 1].map((f, i) => (
        <g key={i}>
          <line x1={P.l} x2={W - P.r} y1={y(max * f)} y2={y(max * f)} stroke={colors.borderSoft} />
          <text x={P.l - 6} y={y(max * f) + 3} textAnchor="end" fontSize="9" fill={colors.textMeta} fontFamily={font.mono}>{fmtAbrev(max * f)}</text>
        </g>
      ))}
      <path d={area} fill={colors.link} opacity="0.08" />
      <path d={linea} fill="none" stroke={colors.link} strokeWidth="2" strokeLinejoin="round" />
      {datos.map((d, i) => (i % cadaN === 0 || i === datos.length - 1) && (
        <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="8.5" fill={colors.textMeta} fontFamily={font.mono}>{d.periodo.length > 7 ? d.periodo.slice(5) : d.periodo}</text>
      ))}
    </svg>
  )
}

// ─── Barras horizontales (rubros / rankings) ─────────────────────────
function Barras({ datos, getLabel, getValor, getSub, color = colors.link, max: maxProp }) {
  if (!datos.length) return <Vacio />
  const max = maxProp || Math.max(...datos.map(getValor), 1)
  return (
    <div>
      {datos.map((d, i) => {
        const v = getValor(d)
        return (
          <div key={i} style={{ padding: '7px 0', borderBottom: i < datos.length - 1 ? `1px solid ${colors.borderSoft}` : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 13.5, color: colors.text, fontFamily: font.sans, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span style={{ color: colors.textMeta, ...mono, marginRight: 6, fontSize: 12 }}>{i + 1}</span>{getLabel(d)}
              </span>
              <span style={{ ...mono, fontSize: 13.5, fontWeight: 700, color: colors.text, flexShrink: 0 }}>{fmtAbrev(v)}</span>
            </div>
            <div style={{ height: 6, background: colors.borderSoft, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${(v / max) * 100}%`, height: '100%', background: color, borderRadius: 3 }} />
            </div>
            {getSub && <div style={{ fontSize: 11, color: colors.textMeta, marginTop: 3 }}>{getSub(d)}</div>}
          </div>
        )
      })}
    </div>
  )
}

function Vacio() { return <div style={{ padding: 24, textAlign: 'center', color: colors.textMeta, fontSize: 13 }}>Sin datos en el período</div> }

function Bloque({ titulo, children }) {
  return (
    <section style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 14, marginTop: 12 }}>
      <h2 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{titulo}</h2>
      {children}
    </section>
  )
}

const TABS = [
  { k: 'vendedor', label: 'Vendedores', color: colors.t80Text },
  { k: 'cliente', label: 'Clientes', color: colors.link },
  { k: 'producto', label: 'Productos', color: PALETA_BI[2] },
]

export default function BiAppVentas() {
  const [preset, setPreset] = useState('anio')
  const [[desde, hasta], setRango] = useState(presetRangoBi('anio'))
  const [tab, setTab] = useState('vendedor')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [gruposDim, setGruposDim] = useState([])   // todos los grupos (dimensión)
  const [grupos, setGrupos] = useState([])          // los elegidos en el filtro

  useEffect(() => {
    biApi.dimensiones().then(({ data }) => setGruposDim(data.grupos || [])).catch(() => {})
  }, [])

  const cargar = useCallback(() => {
    setLoading(true)
    biApi.ventas({ desde, hasta, grupo: grupos })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [desde, hasta, grupos])
  useEffect(() => { cargar() }, [cargar])

  const toggleGrupo = (g) => setGrupos((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]))

  const k = data?.kpis
  const rankings = {
    vendedor: { datos: data?.por_vendedor || [], label: (d) => d.nombre, sub: (d) => `${fmtNum(d.comprobantes)} comprob · ${fmtNum(d.clientes)} clientes` },
    cliente: { datos: data?.por_cliente || [], label: (d) => d.nombre || `Cliente ${d.cod_cli}`, sub: (d) => `${fmtNum(d.comprobantes)} comprobantes` },
    producto: { datos: data?.por_producto || [], label: (d) => d.descripcion, sub: (d) => `${fmtNum(d.unidades)} u · ${(d.rubro || '').toLowerCase()}` },
  }
  const rk = rankings[tab]
  const tabColor = TABS.find((t) => t.k === tab).color

  return (
    <div>
      <h1 style={{ margin: '0 0 12px', fontSize: 20, fontFamily: font.sans, color: colors.text }}>Ventas</h1>

      {/* Presets de período (scroll horizontal) */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' }}>
        {PRESETS_BI.map((p) => {
          const on = preset === p.k
          return (
            <button key={p.k} onClick={() => { setPreset(p.k); setRango(presetRangoBi(p.k)) }}
              style={{ flexShrink: 0, padding: '7px 14px', borderRadius: 999, border: `1px solid ${on ? colors.link : colors.border}`, background: on ? colors.linkBg : colors.bg, color: on ? colors.link : colors.textSec, fontSize: 13, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer' }}>
              {p.label}
            </button>
          )
        })}
      </div>

      {/* Filtro por grupo (empresa/canal) — multi-select, vacío = todos */}
      {gruposDim.length > 0 && (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '6px 0 4px', WebkitOverflowScrolling: 'touch' }}>
          {gruposDim.map((g) => {
            const on = grupos.includes(g)
            return (
              <button key={g} onClick={() => toggleGrupo(g)}
                style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 999, border: `1px solid ${on ? colors.text : colors.border}`, background: on ? colors.text : colors.bg, color: on ? colors.bg : colors.textSec, fontSize: 12, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer' }}>
                {g.toLowerCase()}
              </button>
            )
          })}
        </div>
      )}

      {loading && !data ? (
        <div style={{ padding: 40, textAlign: 'center', color: colors.textMeta }}>Cargando…</div>
      ) : !data ? (
        <div style={{ padding: 40, textAlign: 'center', color: colors.err }}>No se pudieron cargar los datos.</div>
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
          {/* KPIs 2-col */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
            <Kpi label="Facturación" valor={fmtAbrev(k.facturacion)} delta={k.var.facturacion} />
            <Kpi label={`Margen ${(k.margen_pct ?? 0).toFixed(0)}%`} valor={fmtAbrev(k.margen)} delta={k.var.margen} />
            <Kpi label="Comprobantes" valor={fmtNum(k.comprobantes)} delta={k.var.comprobantes} />
            <Kpi label="Unidades" valor={fmtNum(k.unidades)} delta={k.var.unidades} />
          </div>

          <Bloque titulo="Evolución de facturación"><MiniLinea datos={data.tendencia} /></Bloque>

          <Bloque titulo="Por grupo">
            <Barras datos={(data.por_grupo || []).slice(0, 6)} color={PALETA_BI[3]}
              getLabel={(d) => (d.grupo || '').toLowerCase()} getValor={(d) => d.facturacion} />
          </Bloque>

          <Bloque titulo="Por rubro">
            <Barras datos={data.por_rubro.slice(0, 6)} color={colors.t80Text}
              getLabel={(d) => (d.rubro || '').toLowerCase()} getValor={(d) => d.facturacion} />
          </Bloque>

          {/* Rankings con tabs */}
          <section style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {TABS.map((t) => {
                const on = tab === t.k
                return (
                  <button key={t.k} onClick={() => setTab(t.k)}
                    style={{ flex: 1, padding: '8px 0', borderRadius: 9, border: `1px solid ${on ? colors.text : colors.border}`, background: on ? colors.text : colors.bg, color: on ? colors.bg : colors.textSec, fontSize: 12.5, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer' }}>
                    {t.label}
                  </button>
                )
              })}
            </div>
            <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 14 }}>
              <Barras datos={rk.datos.slice(0, 10)} color={tabColor}
                getLabel={rk.label} getValor={(d) => d.facturacion} getSub={rk.sub} />
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
